/**
 * HostPipelineKanban - kanban-style admin board showing where every host
 * sits in the activation pipeline.
 *
 * Columns: Waitlist -> Intake Submitted -> Org Linked -> Awaiting Activation -> LIVE -> Archived
 *
 * Each card has a "Move to ..." dropdown that fires the underlying status
 * change. No drag-drop (simpler, same UX).
 */
import { useMemo } from "react";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, MoveRight, Edit2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { id: "waitlist",   label: "Waitlist",            color: "bg-gray-100 text-gray-700" },
  { id: "intake",     label: "Intake Submitted",    color: "bg-blue-100 text-blue-700" },
  { id: "org_linked", label: "Org Linked",          color: "bg-indigo-100 text-indigo-700" },
  { id: "pending",    label: "Awaiting Activation", color: "bg-amber-100 text-amber-700" },
  { id: "live",       label: "LIVE",                color: "bg-emerald-100 text-emerald-700" },
  { id: "archived",   label: "Archived",            color: "bg-gray-200 text-gray-500" },
];

const headers = () => {
  const t = localStorage.getItem("session_token");
  return t ? { "Content-Type": "application/json", Authorization: `Bearer ${t}` } : { "Content-Type": "application/json" };
};

function classify(user) {
  if (user.archived || user.dashboard_status === "archived") return "archived";
  // LIVE = explicitly active dashboard OR is_host flag flipped on (set by activate-dashboard)
  if (user.dashboard_status === "active" || (user.is_host && user.primary_event_id)) return "live";
  if (user.dashboard_status === "pending_review") return "pending";
  if (user.org_id || user.organization_id) return "org_linked";
  if ((user.pilot_intake_count || 0) > 0 || user.is_pilot) return "intake";
  return "waitlist";
}

export default function HostPipelineKanban({ users = [], onUserUpdated, onAddHost, onEditUser }) {
  const grouped = useMemo(() => {
    // Only host-track users (pilot, is_host, role==host, OR explicitly waitlist)
    const hostTrack = users.filter(u => u.is_pilot || u.is_host || u.role === "host" || u.waitlist_category);
    const g = {};
    COLUMNS.forEach(c => g[c.id] = []);
    hostTrack.forEach(u => {
      const col = classify(u);
      (g[col] = g[col] || []).push(u);
    });
    return g;
  }, [users]);

  const moveTo = async (user, target) => {
    try {
      let body = {};
      let endpoint = `${API}/admin/users/${user.user_id}`;
      if (target === "live") {
        // Find or create event then activate
        if (user.primary_event_id) {
          await fetch(`${API}/admin/events/${user.primary_event_id}/activate-dashboard`, { method: "POST", headers: headers() });
        } else {
          body = { is_pilot: true, plan: "legacy_pilot_free", stripe_bypass: true };
        }
      } else if (target === "archived") {
        if (user.primary_event_id) {
          await fetch(`${API}/admin/events/${user.primary_event_id}/archive-dashboard`, { method: "POST", headers: headers() });
        }
        body = { archived: true };
      } else if (target === "intake") {
        body = { is_pilot: true, plan: "legacy_pilot_free", stripe_bypass: true };
      } else if (target === "waitlist") {
        body = { is_pilot: false, plan: "" };
      }
      if (Object.keys(body).length > 0) {
        const r = await fetch(endpoint, { method: "PATCH", headers: headers(), body: JSON.stringify(body) });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      }
      toast.success(`Moved ${user.name || user.email} to ${target}`);
      onUserUpdated?.();
    } catch (e) {
      toast.error(`Move failed: ${e.message}`);
    }
  };

  return (
    <div data-testid="host-pipeline-kanban">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Host Pipeline</h2>
          <p className="text-xs text-gray-500">Track every pilot from waitlist to LIVE in one place. Click "Move to" to advance.</p>
        </div>
        <Button onClick={onAddHost} className="gap-1.5 bg-black hover:bg-gray-800 text-white" size="sm" data-testid="add-host-btn">
          <Plus className="w-3.5 h-3.5" />Add Host
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 overflow-x-auto">
        {COLUMNS.map(col => (
          <div key={col.id} className="bg-gray-50 rounded-2xl p-2 min-h-[200px]" data-testid={`pipeline-col-${col.id}`}>
            <div className="flex items-center justify-between px-2 pb-2">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-700">{col.label}</h3>
              <Badge className={`${col.color} text-[10px]`}>{(grouped[col.id] || []).length}</Badge>
            </div>
            <div className="space-y-2">
              {(grouped[col.id] || []).map(u => (
                <Card key={u.user_id} className="p-2 bg-white shadow-sm" data-testid={`pipeline-card-${u.user_id}`}>
                  <div className="flex items-start gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-pink-400 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{u.name || u.display_name || u.email?.split("@")[0]}</p>
                      <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  {u.primary_category && (
                    <Badge variant="secondary" className="text-[9px] capitalize mb-1.5">{u.primary_category}</Badge>
                  )}
                  <div className="flex items-center gap-1 flex-wrap">
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) moveTo(u, e.target.value); e.target.value = ""; }}
                      className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white flex-1 min-w-0"
                      data-testid={`move-${u.user_id}`}
                    >
                      <option value="">Move to...</option>
                      {COLUMNS.filter(c => c.id !== col.id).map(c => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => onEditUser?.(u)}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Edit"
                      data-testid={`pipeline-edit-${u.user_id}`}
                    >
                      <Edit2 className="w-3 h-3 text-gray-500" />
                    </button>
                    {col.id === "live" && u.primary_event_id && (
                      <a href={`/host/${u.primary_event_id}`} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-gray-100" title="Open">
                        <ExternalLink className="w-3 h-3 text-gray-500" />
                      </a>
                    )}
                  </div>
                </Card>
              ))}
              {(grouped[col.id] || []).length === 0 && (
                <p className="text-[10px] text-gray-300 italic px-2 py-3 text-center">empty</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
